import { type VariantProps, cva } from 'class-variance-authority';

export type TextType = 'h1' | 'h2' | 'h3' | 'body1' | 'body2' | 'body3' | 'small1' | 'small2' | 'small3' | 'label' | 'caption' | 'overline';

export type TextTone = 'default' | 'muted' | 'primary' | 'white' | 'danger' | 'success' | 'warning' | 'info' | 'basic' | 'subtle' | 'neutral' | 'dim90' | 'dim85' | 'mist90' | 'onPrimary' | 'accent';

export type TextToneCva = TextTone | 'unset';

export type TextStrong = 'inherit' | 'medium' | 'semibold' | 'bold' | 'thin';

export type TextLeading = 'inherit' | 'tight' | 'snug' | 'normal' | 'relaxed' | '6' | '9';

export type TextScale = 'default' | 'smBase' | 'captionToXs';

export const textVariants = cva('', {
  variants: {
    t: {
      h1: 'text-3xl font-bold leading-tight',
      h2: 'text-xl font-bold leading-tight',
      h3: 'text-base font-bold leading-tight',
      body1: 'text-lg font-normal',
      body2: 'text-sm font-normal leading-normal',
      body3: 'text-xs font-normal leading-normal',
      small1: 'text-xs font-bold uppercase tracking-wide leading-tight',
      small2: 'text-xs font-medium leading-tight',
      small3: 'text-[10px] font-normal leading-tight',
      label: 'text-[10px] leading-3 font-bold',
      caption: 'text-[11px] font-normal leading-snug',
      overline: 'text-xs font-medium uppercase tracking-wide leading-tight'
    },
    tone: {
      unset: '',
      default: 'text-text-700',
      muted: 'text-text-400',
      primary: 'text-primary-500',
      white: 'text-text-100',
      danger: 'text-danger-500',
      success: 'text-success-500',
      warning: 'text-warning-500',
      info: 'text-info-500',
      basic: 'text-basic-600',
      subtle: 'text-text-500',
      neutral: 'text-neutral-700',
      dim90: 'text-text-700/90',
      dim85: 'text-text-700/85',
      mist90: 'text-text-100/90',
      onPrimary: 'text-basic-1200',
      accent: 'text-primary-600'
    },
    strong: {
      inherit: '',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
      thin: 'font-thin'
    },
    leading: {
      inherit: '',
      tight: 'leading-tight',
      snug: 'leading-snug',
      normal: 'leading-normal',
      relaxed: 'leading-relaxed',
      '6': 'leading-6',
      '9': 'leading-9'
    },
    scale: {
      default: '',
      smBase: 'sm:text-base',
      captionToXs: 'sm:text-xs'
    }
  },
  defaultVariants: {
    t: 'body2',
    tone: 'unset',
    strong: 'inherit',
    leading: 'inherit',
    scale: 'default'
  }
});

export type TextVariantProps = VariantProps<typeof textVariants>;

export type TextFlow = 'contents' | 'inline' | 'inline-block' | 'block' | 'inline-flex' | 'flex';

export const TEXT_DEFAULT_TAG: Record<TextType, string> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  body1: 'p',
  body2: 'p',
  body3: 'p',
  small1: 'span',
  small2: 'span',
  small3: 'span',
  label: 'label',
  caption: 'span',
  overline: 'span'
};

export const TEXT_ARIA_ROLE: Partial<Record<TextType, string>> = {
  h1: 'heading',
  h2: 'heading',
  h3: 'heading'
};

export const TEXT_ARIA_LEVEL: Partial<Record<TextType, number>> = {
  h1: 1,
  h2: 2,
  h3: 3
};
